import Button from "./Common/Button";
import PendingZoneBadge from "./Common/PendingZoneBadge";
import { useLocation, useNavigate } from "react-router-dom";
import useFleetStore from "../utils/store";
import { useState, useEffect, useRef } from "react";
import LocationIcon from "../../src/assets/icons/location-icon.svg"
import Icon from "../assets/icons/Icon";
import { getUnitZone } from "../utils/unitZoneUtils";
import { unitListReturnState } from "../utils/unitListReturn";


const ZoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-theme" aria-hidden="true">
    <path d="M10.3853 2.2553L11.7186 2.955C13.1531 3.70776 13.8703 4.08414 14.2686 4.76048C14.6668 5.43682 14.6668 6.27843 14.6668 7.96166V8.03964C14.6668 9.72287 14.6668 10.5645 14.2686 11.2408C13.8703 11.9172 13.1531 12.2935 11.7186 13.0463L10.3853 13.746C9.21489 14.3602 8.62968 14.6673 8.00016 14.6673C7.37065 14.6673 6.78544 14.3602 5.61502 13.746L4.28169 13.0463C2.84725 12.2935 2.13003 11.9172 1.73176 11.2408C1.3335 10.5645 1.3335 9.72287 1.3335 8.03964V7.96166C1.3335 6.27843 1.3335 5.43682 1.73176 4.76048C2.13003 4.08414 2.84725 3.70776 4.28169 2.955L5.61502 2.25529C6.78544 1.64109 7.37065 1.33398 8.00016 1.33398C8.62968 1.33398 9.21489 1.64109 10.3853 2.2553Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M14 5L8 8M8 8L2 5M8 8V14.3333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const UnassociatedUnitsCard = (props) => {
  const {
    info,
    handleDeleteClick,
    handleAddOrChangeZone,
    isActionsDisabled,
  } = props
  const assignedZone = getUnitZone(info);
  const { allPodData } = useFleetStore();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToManageUnits = unitListReturnState("manageunits", location.search);
  const setSelectedUnit = useFleetStore((state) => state.setSelectedUnit);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation(); // Prevent immediate closing
    setIsOpen((prev) => !prev);
  };

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const goToUnitDetails = () => {
    navigate(`/units/${info.serialNumber}`, {
      state: { ...returnToManageUnits, pendingZone: info.pendingZone || null },
    });
  };

  const handleButtonClick = (e) => {
    e?.stopPropagation();
    setSelectedUnit(info);
    navigate('/configureUnits', { state: returnToManageUnits });
  }

  const handleEditClick = (e, info) => {
    e.preventDefault();
    navigate(
      `/EditUnit?unid=${info?.zone?.unid}&serialNumber=${info?.serialNumber}`,
      { state: returnToManageUnits }
    );
  }

  return (
    <>
      {!info?.zone ? (
        <div data-testid={`unit-card-${info.serialNumber}`} className="units bg-white dark:darkThemeGradiantBg px-4 py-3 rounded-lg shadow-appShadow min-h-48  group">
          <div className="flex flex-col h-full gap-2">
            <div
              className={`unitsBody flex grow cursor-pointer ${info?.metadata?.propertyUnid ? "items-start justify-start" : "items-center justify-center"}`}
              onClick={goToUnitDetails}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goToUnitDetails();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={`flex flex-col gap-3 w-full ${info?.metadata?.propertyUnid ? "items-start" : "items-center"}`}>
                <p className={`unitNumber text-[#1E2A2C] dark:text-white font-semibold font-sans text-lg leading-4 lg:text-xl lg:leading-5 tracking-[0.03px] pt-2 pb-1 ${info?.activated ? "" : "opacity-50"}`} data-testid={`unit-${info.serialNumber}-name`}>
                  {info.serialNumber}
                </p>
                {info?.metadata?.propertyUnid && (
                  <div className="flex gap-2 items center">
                    <div className="iocnHolder flex shrink-0 pt-1">
                      <Icon src={LocationIcon} alt="LocationIcon" className="w-4 h-4" />
                    </div>
                    <p 
                    className="unitNumber text-dark-grey dark:text-white text-sm leading-6 font-medium tracking-[0.021px] line-clamp-2 text-ellipsis whitespace-normal break-words w-60"
                    title={[info?.metadata?.propertyName, info?.metadata?.buildingName, info?.metadata?.unit, info?.metadata?.floor ? `Floor ${info?.metadata.floor}` : null, info?.metadata?.room ? `Room ${info?.metadata.room}` : null]
                        .filter(Boolean)
                        .join(", ")}
                    >
                      {[info?.metadata?.propertyName, info?.metadata?.buildingName, info?.metadata?.unit, info?.metadata?.floor ? `Floor ${info?.metadata.floor}` : null, info?.metadata?.room ? `Room ${info?.metadata.room}` : null]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                )}
                {assignedZone && (
                  <div className="flex items-center gap-2 w-full">
                    <div className="iconHolder shrink-0">
                      <ZoneIcon />
                    </div>
                    <p
                      className="text-dark-grey dark:text-white text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[0.024px] truncate whitespace-nowrap w-60 min-w-0 overflow-hidden"
                      data-testid={`unit-${info.serialNumber}-pending-zone`}
                    >
                      {assignedZone.name}
                    </p>
                    <PendingZoneBadge className="size-4 shrink-0 ml-auto" data-testid={`unit-${info.serialNumber}-pending-zone-badge`} />
                  </div>
                )}
                {!info?.metadata?.propertyUnid && (
                  <Button
                    data-testid={`unit-${info.serialNumber}-configure-button`}
                    className="text-[#FF8C65] text-base cursor-pointer leading-4 lg:text-lg lg:leading-5 font-semibold font-sans hidden group-hover:flex"
                    onClick={handleButtonClick}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    Configure
                  </Button>
                )}
              </div>
            </div>
            <div className="unitsFooter border-t border-[#AFC3BC66] dark:border-darkThemeBorder basis-9">
              <div className="flex items-center justify-between h-full pt-3">
                <div className="connectionsIndicator pt-3">
                </div>
                {info?.metadata?.propertyUnid && (
                  <div className="dropdownHolder relative" ref={dropdownRef}>
                    <button
                      data-testid={`unit-${info.serialNumber}-actions-toggle`}
                      onClick={(e) => {
                        if (isActionsDisabled) return;
                        toggleDropdown(e);
                      }}
                      disabled={isActionsDisabled}
                      className={`flex items-center gap-2 text-primaryText font-semibold text-sm leading-6 tracking-[0.021px] ${
                        isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      aria-disabled={isActionsDisabled}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="5" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                        <circle cx="12" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                        <circle cx="19" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                      </svg>
                    </button>
                    {!isActionsDisabled && isOpen && (
                      <div className="dropdownContent min-w-24 text-nowrap absolute bottom-full right-0 bg-[#FFF4D1] dark:bg-darkThemeDropdownBg dark:shadow-darkThemeDropdownMenuShadow rounded-lg shadow-appShadow block" data-testid={`unit-${info.serialNumber}-actions-menu`}>
                        <ul className="flex flex-col">
                          {info?.metadata?.propertyUnid && allPodData?.zones?.length > 0 && (
                            <li
                              data-testid={`unit-${info.serialNumber}-change-zone`}
                              className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded border-b border-grey dark:border-darkThemeBorder last:border-b-0"
                              onClick={(e) => handleAddOrChangeZone(e, info)}
                            >
                              {assignedZone ? "Change Zone" : "Add to Zone"}
                            </li>
                          )}
                          <li
                            data-testid={`unit-${info.serialNumber}-edit`}
                            className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded border-b border-grey dark:border-0"
                            onClick={handleButtonClick}
                          >
                            Edit Unit
                          </li>
                          {assignedZone && (
                            <li
                              data-testid={`unit-${info.serialNumber}-remove-from-zone`}
                              className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded"
                              onClick={(e) => handleDeleteClick(e, info)}
                            >
                              Remove from zone
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div data-testid={`unit-card-${info.serialNumber}`} className="units bg-white dark:darkThemeGradiantBg px-4 py-3 rounded-lg shadow-appShadow min-h-48">
          <div className="flex flex-col h-full gap-2">
            <div
              className="unitsBody flex grow pb-2 cursor-pointer"
              onClick={goToUnitDetails}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goToUnitDetails();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="flex flex-col gap-3">
                <p className="unitNumber text-dark-grey dark:text-white font-semibold text-lg leading-4 lg:text-xl lg:leading-5 tracking-[0.03px] pt-2 pb-1" data-testid={`unit-${info.serialNumber}-name`}>
                  {info.serialNumber}
                </p>
                {info?.metadata?.propertyUnid && (
                  <div className="flex gap-2 items center">
                    <div className="iocnHolder flex shrink-0 pt-1">
                      <Icon src={LocationIcon} alt="LocationIcon" className="w-4 h-4" />
                    </div>
                    <p 
                    className="unitNumber text-dark-grey dark:text-white text-sm leading-6 font-medium tracking-[0.021px] line-clamp-2 text-ellipsis whitespace-normal break-words w-60"
                    title={[info?.metadata?.propertyName, info?.metadata?.buildingName, info?.metadata?.unit, info?.metadata?.floor ? `Floor ${info?.metadata.floor}` : null, info?.metadata?.room ? `Room ${info?.metadata.room}` : null]
                        .filter(Boolean)
                        .join(", ")}
                    >
                      {[info?.metadata?.propertyName, info?.metadata?.buildingName, info?.metadata?.unit, info?.metadata?.floor ? `Floor ${info?.metadata.floor}` : null, info?.metadata?.room ? `Room ${info?.metadata.room}` : null]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <div className="iconHolder">
                    <ZoneIcon />
                  </div>
                  <p className="text-dark-grey dark:text-white text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[0.024px] truncate whitespace-nowrap w-60 min-w-0 overflow-hidden">
                    {info?.zone?.name}
                  </p>
                </div>
              </div>
            </div>
            <div className="unitsFooter border-t border-grey dark:border-darkThemeBorder basis-9">
              <div className="flex items-center justify-between h-full pt-3">
                <div className="connectionsIndicator">
                </div>
                <div className="dropdownHolder relative" ref={dropdownRef}>
                  <button
                    data-testid={`unit-${info.serialNumber}-actions-toggle`}
                    onClick={(e) => {
                      if (isActionsDisabled) return;
                      toggleDropdown(e);
                    }}
                    disabled={isActionsDisabled}
                    className={`flex items-center gap-2 text-primaryText font-semibold text-sm leading-6 tracking-[0.021px] ${
                      isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    aria-disabled={isActionsDisabled}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="5" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                      <circle cx="12" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                      <circle cx="19" cy="12" r="2" fill={isActionsDisabled ? "#AFC3BC" : "#FF8C65"}></circle>
                    </svg>
                  </button>
                  {!isActionsDisabled && isOpen && (
                    <div className="dropdownContent min-w-24 text-nowrap absolute bottom-full right-0 bg-[#FFF4D1] dark:bg-darkThemeDropdownBg rounded-lg shadow-appShadow dark:shadow-darkThemeDropdownMenuShadow block" data-testid={`unit-${info.serialNumber}-actions-menu`}>
                      <ul className="flex flex-col">
                        {info?.metadata?.propertyUnid && allPodData?.zones?.length > 0 && (
                          <li
                            data-testid={`unit-${info.serialNumber}-change-zone`}
                            className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded border-b border-grey dark:border-darkThemeBorder"
                            onClick={(e) => handleAddOrChangeZone(e, info)}
                          >
                            Change Zone
                          </li>
                        )}
                        <li
                          data-testid={`unit-${info.serialNumber}-edit`}
                          className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded border-b border-grey dark:border-darkThemeBorder"
                          onClick={(e) => handleEditClick(e, info)}
                        >
                          Edit Unit
                        </li>
                        <li
                          data-testid={`unit-${info.serialNumber}-remove-from-zone`}
                          className="text-dark-grey dark:text-white font-medium text-sm leading-6 cursor-pointer px-3 py-1 rounded"
                          onClick={(e) => handleDeleteClick(e, info)}
                        >
                          Remove from zone
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default UnassociatedUnitsCard;
